import makeMap from "@qdabra/forms-shared/makeMap";
import { isEmpty, path, prop } from "ramda";
import { mergeColumns } from "../../../utility/arrayHelpers";
import { buildRestBatch, parseRestBatchResult, performBatchQuery } from "../../../utility/sharePoint/sharePoint.restBatch";
import { buildPartitionedListQueryUrls } from "./list.rest.buildUrl";
import { collectFieldValues, listDateFields, replaceFieldValues } from "./list.rest.dates";

const MAX_URL_LENGTH = 2048;

// [FilterField] -> { String: String }
const getFilterValues = makeMap(
    path(['field', 'internalName']),
    prop('value'),
);

const fixupDates = async (shpAccess, siteUrl, fields, items) => {
    const dateFields = listDateFields(fields);

    if (isEmpty(dateFields)) {
        return items;
    }

    const dateValues = collectFieldValues(dateFields, items);

    if (isEmpty(dateValues)) {
        return items;
    }

    const translatedValues = await shpAccess.translateSharePointValuesAsync(
        siteUrl,
        { dateTimes: dateValues },
    );

    return replaceFieldValues(
        dateFields,
        translatedValues.dateTimes,
        items,
    );
};

export const buildBatchQuery = (
    {
        siteUrl,
        fieldDefinitions,
        sortBy,
        sortAsc,
    },
    useListName,
    listIdentifier,
    filterValues,
    maxLength,
) => {
    const urls = buildPartitionedListQueryUrls(
        siteUrl,
        fieldDefinitions,
        sortBy,
        sortAsc,
        useListName,
        listIdentifier,
        filterValues,
        maxLength,
    );

    const requests = urls.map((url) => ({ url }));

    return buildRestBatch(requests);
};

export const processBatchResult = (response) => {
    const parts = parseRestBatchResult(response);

    const errors = parts.map(prop('error')).filter(Boolean);

    if (!isEmpty(errors)) {
        throw new Error(
            'Encountered error when querying list data: ' + errors.join(', '),
        );
    }

    const arrays = parts.map(prop('value'));

    return mergeColumns(arrays);
};

const queryBatched = async (shpAccess, queryValues) => {
    const {
        siteUrl,
        listId,
        listName,
        filterFields,
    } = queryValues;

    const request = buildBatchQuery(
        queryValues,
        !!listName,  // useListName
        listName || listId,
        getFilterValues(filterFields),
        MAX_URL_LENGTH,
    );

    const result = await performBatchQuery(
        shpAccess,
        siteUrl,
        request,
        { 
            listId: listName ? null : listId,
            listName,
        },
    );

    return processBatchResult(result);
}

const query = async (
    shpAccess,
    queryValues,
) => {
    // 2022-04-07 Removed code for single REST query
    const { siteUrl, fieldDefinitions } = queryValues;

    const result = await queryBatched(shpAccess, queryValues);

    const itemsWithDates = await fixupDates(
        shpAccess,
        siteUrl,
        fieldDefinitions,
        result,
    )

    return { items: itemsWithDates };
};


export default query;